import type { EmployeeSnapshot } from '@klukka/client';
import { dow, fmt, fmtClock, fmtDate, fmtDay, fmtH, t, type LangCode } from '@klukka/core';
import { Ic } from '@klukka/ui';
import type { EmployeeView } from '../derive';
import { totalHours } from '../derive';

/**
 * The screen the three-second rule is about: one button, then the two numbers
 * a field worker actually wants — hours today and what today is worth.
 */
export function HomeTab({ snap, view, lang, busy, onToggleClock }: {
  snap: EmployeeSnapshot;
  view: EmployeeView;
  lang: LangCode;
  busy: boolean;
  onToggleClock: () => void;
}) {
  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);
  const { now, todayRow, running, live } = view;
  const loggedToday = snap.employee.worklog[snap.serverToday]?.h ?? 0;

  return (
    <div className="ph-body">
      <button className={`clock-btn${running ? ' on' : ''}`} disabled={busy} onClick={onToggleClock}>
        <span className="clock-ic">{running ? <Ic.stop size={26} sw={2} /> : <Ic.play size={26} sw={2} />}</span>
        <span className="clock-lbl">{running ? tr('clockOut') : tr('clockIn')}</span>
        {running ? (
          <span className="clock-timer">{fmtClock(live)}</span>
        ) : (
          <span className="clock-sub"><Ic.pin size={13} /> {snap.company.name}</span>
        )}
      </button>

      {running && snap.shift ? (
        <div className="working-note">
          {tr('working')} · {tr('since')} {new Date(snap.shift.clockInAt).toTimeString().slice(0, 5)}
          {snap.shift.otConfirmedAt ? (
            <span className="chip" style={{ borderColor: 'var(--live)', color: 'var(--live)', marginLeft: 8 }}>
              {tr('otConfirmed')} {new Date(snap.shift.otConfirmedAt).toTimeString().slice(0, 5)}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="card">
        <div className="split">
          <Num
            label={tr('hoursToday')}
            value={`${fmtH(todayRow?.h ?? 0)} ${tr('hours')}`}
            big
            // Today can be a running clock plus hours already logged or corrected —
            // show the composition rather than an unexplained sum.
            sub={running && loggedToday > 0 ? `${fmtH(live)} + ${fmtH(loggedToday)}` : undefined}
          />
          <Num label={tr('sincePayout')} value={`${fmtH(totalHours(now.calc))} ${tr('hours')}`} big />
        </div>
        <div className="split">
          <Num label={tr('earnedToday')} value={fmt(todayRow?.gross ?? 0)} />
          <Num label={tr('estNet')} value={fmt(todayRow?.net ?? 0)} accent />
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          {tr('period')} · {fmtDay(now.pinfo.start)}–{fmtDay(now.pinfo.end)}
        </div>
        {now.adjSum !== 0 ? (
          <div className="rowline dim">
            <span>± {tr('adjustments')}</span>
            <b style={{ color: now.adjSum > 0 ? 'var(--ok)' : 'var(--danger)' }}>
              {now.adjSum > 0 ? '+' : ''}{fmt(now.adjSum)}
            </b>
          </div>
        ) : null}
        <div className="rowline"><span>{tr('gross')}</span><b>{fmt(now.calc.gross)}</b></div>
        <div className="rowline hero"><span>{tr('estNet')}</span><b style={{ color: 'var(--primary)' }}>{fmt(now.calc.net)}</b></div>
        <div className="rowline dim"><span>{tr('employerCost')}</span><b>{fmt(now.calc.totalCost)}</b></div>
        <div className="rowline dim"><span>{tr('nextPayout')}</span><b>{fmtDate(view.payout.now)}</b></div>

        <table className="mini-tbl">
          <thead>
            <tr>
              <th /><th /><th>DV</th><th>YV1</th><th>YV2</th>
              <th style={{ textAlign: 'right' }}>{tr('gross').split(' ')[0]}</th>
            </tr>
          </thead>
          <tbody>
            {now.calc.rows.filter((r) => r.gross > 0).map((r) => (
              <tr key={r.date}>
                <td><b>{fmtDay(r.date)}</b></td>
                <td className="dimtx">{dow(lang, r.dow)}</td>
                <td className="mono">{r.dvH ? fmtH(r.dvH) : '—'}</td>
                <td className="mono" style={r.yv1H ? { color: 'var(--live)', fontWeight: 600 } : undefined}>{r.yv1H ? fmtH(r.yv1H) : '—'}</td>
                <td className="mono" style={r.yv2H ? { color: 'var(--ot2)', fontWeight: 600 } : undefined}>{r.yv2H ? fmtH(r.yv2H) : '—'}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{fmt(r.gross)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="note-est">{tr('estimateNote')}</div>
    </div>
  );
}

function Num({ label, value, big, accent, sub }: {
  label: string;
  value: string;
  big?: boolean;
  accent?: boolean;
  sub?: string;
}) {
  return (
    <div className={`num${big ? ' big' : ''}`}>
      <div className="num-label">{label}</div>
      <div className="num-value" style={accent ? { color: 'var(--primary)' } : undefined}>{value}</div>
      {sub ? <div className="num-sub mono">{sub}</div> : null}
    </div>
  );
}
